// レビュー依頼メールの送信設定・送信キュー API
// 対応ルーター: backend/routers/ugc_mail.py

export const UGCMailApiPath = {
  GET_SENDER: '/api/sites/{site_id}/ugc/mail/sender',
  UPDATE_SENDER: '/api/sites/{site_id}/ugc/mail/sender',
  GET_QUEUE: '/api/sites/{site_id}/ugc/mail/queue?status={status}&limit={limit}&offset={offset}',
  DISPATCH: '/api/sites/{site_id}/ugc/mail/dispatch',
  RETRY: '/api/sites/{site_id}/ugc/mail/retry',
  GET_PREVIEW: '/api/sites/{site_id}/ugc/mail/preview?template_id={template_id}',
  TEST_SEND: '/api/sites/{site_id}/ugc/mail/test-send',
  MANUAL_REQUEST: '/api/sites/{site_id}/ugc/mail/request',
  INCENTIVE_CSV: '/api/sites/{site_id}/ugc/mail/incentive-csv?date_from={date_from}&date_to={date_to}',
  INCENTIVE_TARGETS: '/api/sites/{site_id}/ugc/mail/incentive-targets?limit={limit}',
  INCENTIVE_MARK: '/api/sites/{site_id}/ugc/mail/incentive-mark'
} as const

/** off=送らない / test=テスト送信先にだけ送る / live=本番の顧客へ送る */
export type SendingMode = 'off' | 'test' | 'live'

/** 送信待ち＝これから送る、送信済＝送れた、失敗＝再送できる、見送り＝送らないと決めた */
export type MailQueueStatus = 'pending' | 'sent' | 'failed' | 'skipped'

export interface GetSenderVars {
  site_id: number
}

export interface UpdateSenderVars {
  site_id: number
  from_name?: string | null
  from_email?: string | null
  reply_to?: string | null
  sending_mode: SendingMode
  test_email?: string | null
  daily_limit?: number | null
  footer_text?: string | null
  skip_unavailable_products?: boolean
  group_by_order?: boolean
}

export interface MailSenderSettingResponse {
  site_id: number
  from_name: string | null
  from_email: string | null
  reply_to: string | null
  sending_mode: SendingMode
  test_email: string | null
  /** null は制限なし（既定）。送信は15分ごとの小分けで分散される */
  daily_limit: number | null
  footer_text: string | null
  /** 販売終了・非公開になった商品に依頼を送らない（既定 true） */
  skip_unavailable_products: boolean
  /** 1注文に複数商品があっても1通にまとめる（既定 true） */
  group_by_order: boolean
  /** 送信の前提で足りていないもの。空配列なら送れる状態 */
  problems: string[]
  /** 共通メール配信サーバ（SMTP）が登録済みか。サイト側では直せない */
  smtp_configured: boolean
  sent_today: number
}

export interface GetQueueVars {
  site_id: number
  status: MailQueueStatus
  limit: number
  offset: number
}

/** auto=出荷から自動生成 / manual=画面から個別に依頼 */
export type MailSource = 'auto' | 'manual'

export interface MailQueueItem {
  id: number
  status: string
  /** initial / followup1 / followup2 */
  kind: string
  /** 同じ種別の何回目の送信か。再送すると 2, 3 と増える */
  attempt_no: number
  source: MailSource
  customer_email: string | null
  customer_name: string | null
  product_name: string | null
  order_id: string | null
  error_message: string | null
  skip_reason: string | null
  sent_subject: string | null
  sent_to: string | null
  /** いつ送る予定か */
  scheduled_at: string | null
  sent_at: string | null
  opened_at: string | null
  clicked_at: string | null
  /** レビューが投稿された日時（依頼が持つ） */
  submitted_at: string | null
}

export interface MailQueueResponse {
  counts: { pending: number; sent: number; failed: number; skipped: number }
  items: MailQueueItem[]
  total: number
}

export interface DispatchVars {
  site_id: number
}

export interface DispatchResponse {
  sent: number
  failed: number
  skipped: number
  /** 1通も送らなかったときの理由。null なら送るものが無かっただけ */
  reason: string | null
}

export interface RetryVars {
  site_id: number
  log_ids?: number[] | null
}

export interface RetryResponse {
  restored: number
}

export interface GetPreviewVars {
  site_id: number
  /** 指定した文面でプレビューする。未指定は既定の文面 */
  template_id?: number
}

export interface MailPreviewResponse {
  subject: string
  html: string
  sample_customer_name: string
  sample_product_name: string
}

export interface TestSendVars {
  site_id: number
  to?: string | null
}

export interface TestSendResponse {
  ok: boolean
  to: string | null
  subject: string | null
  error: string | null
}

export interface ManualRequestVars {
  site_id: number
  customer_email: string
  customer_name?: string | null
  product_external_id?: string | null
  product_name?: string | null
  order_id?: string | null
  template_id?: number | null
}

export interface ManualRequestResponse {
  ok: boolean
  /** 送信待ちに積んだか。送信モードが off のときは積まず URL だけ返る */
  queued: boolean
  form_url: string | null
  subject: string | null
  note: string | null
}

export interface IncentiveCsvVars {
  site_id: number
  date_from: string
  date_to: string
}

export interface IncentiveTargetsVars {
  site_id: number
  limit: number
}

export interface IncentiveTarget {
  id: number
  submitted_at: string | null
  customer_name: string | null
  customer_email: string | null
  order_id: string | null
  product_external_id: string | null
  product_name: string | null
  rating: number | null
  source: MailSource
}

export interface IncentiveTargetsResponse {
  items: IncentiveTarget[]
  total: number
  processed_total: number
}

export interface IncentiveMarkVars {
  site_id: number
  ids: number[]
}

export interface IncentiveMarkResponse {
  marked: number
}
